import { useMemo } from 'react';
import { DevHubAlert, DevHubBox } from '@devxdiscover/devhub-ui';
import useGoogleAnalytics from '../../util/useGoogleAnalytics';
import { GA4_EVENTS } from '../../util/analyticsEvents';
import { Progress } from '@backstage/core-components';
import { Workbox } from '../home/Workbox';
import { useHierarchyApplications, useResourceExport } from './hooks';
import {
  UserSearchSection,
  ReportHeader,
  ExportProgress,
  SummaryStatisticsCards,
  ApplicationsTable,
} from './components';
import { HierarchyLevel, Severity, TargetUserDisplay } from './types';
import { FILETYPE, HIERARCHY_LEVEL_LABELS } from './utils/constants';

/**
 * CGAHierarchyReport component provides hierarchy-based reporting functionality
 * for CGA (Compliance/Governance/Audit) purposes.
 *
 * Features:
 * - Search for any user to generate their hierarchy report
 * - View applications by organizational hierarchy (Direct Reports / Full Reporting Hierarchy)
 * - Export comprehensive reports in CSV/Excel format with query audit metadata
 * - Point-in-time asset inventory generation
 */
export const CGAHierarchyReport = () => {
  // Custom hooks for state management
  const [appState, appActions] = useHierarchyApplications();
  const [exportState, exportActions] = useResourceExport();
  const { sendEvent } = useGoogleAnalytics();

  // Compute target user display information
  const targetUserDisplay: TargetUserDisplay = useMemo(() => {
    const nameFromParty = [
      appState.selectedParty?.firstName,
      appState.selectedParty?.lastName,
    ]
      .filter(Boolean)
      .join(' ');

    const name =
      appState.targetUserName || nameFromParty || appState.targetLogin;
    return {
      name,
      title: appState.targetUserTitle,
      login: appState.targetLogin,
    };
  }, [
    appState.selectedParty?.firstName,
    appState.selectedParty?.lastName,
    appState.targetLogin,
    appState.targetUserName,
    appState.targetUserTitle,
  ]);

  // Compute hierarchy level label for display
  const hierarchyLevelLabel = useMemo(() => {
    return appState.hierarchyLevel === HierarchyLevel.DIRECT_REPORTS
      ? HIERARCHY_LEVEL_LABELS.DIRECT_REPORTS
      : HIERARCHY_LEVEL_LABELS.FULL_REPORTING_HIERARCHY;
  }, [appState.hierarchyLevel]);

  // Compute application statistics
  const statistics = useMemo(() => {
    const activeCount = appState.applications.filter(
      app => app.status?.toLowerCase() === 'active',
    ).length;
    const preProdCount = appState.applications.filter(
      app => app.status?.toLowerCase() === 'pre-prod',
    ).length;
    return {
      total: appState.applications.length,
      active: activeCount,
      preProd: preProdCount,
      selected: appState.selectedApps.length,
    };
  }, [appState.applications, appState.selectedApps.length]);

  // Handle Generate Report with validation
  const handleGenerateReport = async () => {
    if (!appState.selectedParty?.login) {
      exportActions.handleAlert({
        open: true,
        message: 'Please select a user to generate the report.',
        severity: 'error',
      });
      return;
    }
    await appActions.handleGenerateReport();
  };

  // Handle export format change
  const handleExportFormatChange = (
    event: React.ChangeEvent<{ value: unknown }>,
  ) => {
    exportActions.setExportFormat(event.target.value as FILETYPE);
  };

  // Handle export
  const handleExport = () => {
    exportActions.handleExportFullReport(
      appState.applications,
      appState.selectedApps,
      appState.targetLogin,
      appState.hierarchyLevel,
    );
    if (appState.applications.length > 0) {
      sendEvent({
        action: GA4_EVENTS.CGA_REPORT_GENERATED,
        subject: appState.targetLogin,
        attributes: {
          totalCount: appState.applications.length,
          hierarchyLevel: appState.hierarchyLevel,
        },
      });
    }
  };

  // Handle clear search with alert
  const handleClearSearch = () => {
    // Dismiss any stale alert before clearing search
    exportActions.closeAlert();
    appActions.handleClearSearch();
    exportActions.handleAlert({
      open: true,
      message: 'Search cleared.',
      severity: 'info',
    });
  };

  return (
    <Workbox
      color="purple"
      title="CGA Hierarchy Report"
      info=""
      component="CGAHierarchyReport"
      subHeader="Generate hierarchy reports for users and their reporting structure."
    >
      <>
        {appState.loading && <Progress />}
        {appState.error && (
          <DevHubAlert severity="error">
            Error fetching applications: {appState.error.message}
          </DevHubAlert>
        )}

        {/* User Search Section */}
        <UserSearchSection
          selectedParty={appState.selectedParty}
          onPartySelect={appActions.handlePartySelect}
          hierarchyLevel={appState.hierarchyLevel}
          onHierarchyChange={appActions.handleHierarchyChange}
          onGenerateReport={handleGenerateReport}
          onClear={handleClearSearch}
          hasSearched={appState.hasSearched}
          targetLogin={appState.targetLogin}
          loading={appState.loading}
          partySearchResetKey={appState.partySearchResetKey}
        />

        {/* Alert Display — rendered outside hasSearched so alerts remain visible after clear */}
        <DevHubBox mt={2}>
          {exportState.alert.open && (
            <DevHubAlert
              variant="outlined"
              severity={exportState.alert.severity as Severity}
              style={{ marginBottom: '16px' }}
              onClose={exportActions.closeAlert}
            >
              {exportState.alert.message}
            </DevHubAlert>
          )}
        </DevHubBox>

        {/* Show the rest only after a search */}
        {appState.hasSearched && appState.targetLogin && (
          <>
            {!appState.loading &&
              !appState.error &&
              appState.applications.length > 0 && (
                <ReportHeader
                  targetUserDisplay={targetUserDisplay}
                  hierarchyLevelLabel={hierarchyLevelLabel}
                  exportFormat={exportState.exportFormat}
                  onExportFormatChange={handleExportFormatChange}
                  onExport={handleExport}
                  onCancel={exportActions.handleCancelExport}
                  isExporting={exportState.isExporting}
                  selectedCount={appState.selectedApps.length}
                  totalCount={appState.applications.length}
                  loading={appState.loading}
                />
              )}

            {/* Export Progress Section */}
            <ExportProgress
              progress={exportState.exportProgress}
              stage={exportState.exportStage}
              appCount={exportState.exportAppCount}
              isExporting={exportState.isExporting}
            />

            {/* Summary Statistics Cards */}
            {!appState.loading &&
              !appState.error &&
              appState.applications.length > 0 && (
                <SummaryStatisticsCards
                  totalCount={statistics.total}
                  activeCount={statistics.active}
                  preProdCount={statistics.preProd}
                  selectedCount={statistics.selected}
                />
              )}

            {/* No Applications Alert */}
            {!appState.loading &&
              !appState.error &&
              appState.applications.length === 0 && (
                <DevHubBox mb={2}>
                  <DevHubAlert severity="info">
                    No applications found with status Active or Pre-Prod for
                    this user.
                  </DevHubAlert>
                </DevHubBox>
              )}

            {/* Applications Table */}
            {!appState.loading &&
              !appState.error &&
              appState.applications.length > 0 && (
                <ApplicationsTable
                  applications={appState.applications}
                  selectedApps={appState.selectedApps}
                  onSelectionChange={appActions.setSelectedApps}
                />
              )}
          </>
        )}
      </>
    </Workbox>
  );
};
